import { isEncompass } from '$Utilities/integrationPlatform';
import { normalizeToStartOfDay } from '$Utilities/dateUtility';
import { Box, Button } from '@mui/material';
import { React } from 'Imports';
import classNames from 'classnames';
import { useEffect } from 'react';

const styles = require('./PresetDateRangeButtons.scss') as {
    containerEncompass: string;
    containerGeotab: string;
    buttonBase: string;
    buttonActive: string;
    buttonInactive: string;
};

type PresetType = 'today' | 'last14' | 'last30' | null;

interface IPresetButtonsProps {
    dateRange: {
        startDate: Date;
        endDate: Date;
    };
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

export const PresetDateRangeButtons: React.FC<IPresetButtonsProps> = ({ dateRange, onDateRangeChange }) => {
    const getButtonClass = (preset: PresetType) => {
        return classNames(styles.buttonBase, activePreset === preset ? styles.buttonActive : styles.buttonInactive);
    };

    const [activePreset, setActivePreset] = React.useState<PresetType>(null);

    const formatDate = (date: Date) => {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    };

    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            const today = new Date();
            const last14Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13);
            const last30Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);

            const startDateFormatted = formatDate(dateRange.startDate);
            const endDateFormatted = formatDate(dateRange.endDate);

            if (startDateFormatted === formatDate(today) && endDateFormatted === formatDate(today)) {
                setActivePreset('today');
            } else if (startDateFormatted === formatDate(last14Days) && endDateFormatted === formatDate(today)) {
                setActivePreset('last14');
            } else if (startDateFormatted === formatDate(last30Days) && endDateFormatted === formatDate(today)) {
                setActivePreset('last30');
            } else {
                setActivePreset(null);
            }
        }
    }, [dateRange]);

    const handleTodayClicked = () => {
        const today = normalizeToStartOfDay(new Date());
        onDateRangeChange(today, today);
    };

    const handleLast14DaysClicked = () => {
        const today = normalizeToStartOfDay(new Date());
        const last14Days = new Date(today);
        last14Days.setDate(today.getDate() - 13);
        onDateRangeChange(last14Days, today);
    };

    const handleLast30DaysClicked = () => {
        const today = normalizeToStartOfDay(new Date());
        const last30Days = new Date(today);
        last30Days.setDate(today.getDate() - 29);
        onDateRangeChange(last30Days, today);
    };

    return (
        <Box
            className={isEncompass ? styles.containerEncompass : styles.containerGeotab}
            data-testid={'date-range-picker-preset-range-buttons-container'}
        >
            <Button
                variant="text"
                size="small"
                data-testid={'date-range-picker-preset-range-button-today'}
                className={getButtonClass('today')}
                onClick={handleTodayClicked}
                disableRipple
            >
                Today
            </Button>
            <Button
                variant="text"
                size="small"
                data-testid={'date-range-picker-preset-range-button-last14'}
                className={getButtonClass('last14')}
                onClick={handleLast14DaysClicked}
                disableRipple
            >
                Last 14 Days
            </Button>
            <Button
                variant="text"
                size="small"
                data-testid={'date-range-picker-preset-range-button-last30'}
                className={getButtonClass('last30')}
                onClick={handleLast30DaysClicked}
                disableRipple
            >
                Last 30 Days
            </Button>
        </Box>
    );
};

export default PresetDateRangeButtons;
